import { contrastOn, pageFrame } from './profile.js'
import { SPRITES } from '../globals/themeSprites.js'

/**
 * Every style a profile theme produces, as functions of the theme.
 *
 * These lived as computeds on `Profile.vue` back when that file was the whole
 * page. Splitting the page into route panels made them shared, and a shared
 * computed has to live somewhere that isn't a component — otherwise the shell
 * computes them and passes a dozen style objects down as props, which is the
 * same coupling with more typing.
 *
 * All pure, all take the theme, none touch `this`. `mixins/themedPanel.js`
 * wires the common ones onto a component's `theme` prop; a panel that needs
 * one of the specific ones (the card shapes, the header) imports it directly.
 */

/**
 * Card shapes a border-radius can't describe. Both are clip-paths, and a
 * clip-path removes the element's border along with everything else outside
 * the shape — which is why a clipped card takes its frame from a plate
 * behind it (boxFrameStyles) rather than a border of its own.
 */
const BOX_CLIPS = Object.freeze({
    pixel: 'polygon(0 7px,7px 7px,7px 0,calc(100% - 7px) 0,calc(100% - 7px) 7px,100% 7px,100% calc(100% - 7px),calc(100% - 7px) calc(100% - 7px),calc(100% - 7px) 100%,7px 100%,7px calc(100% - 7px),0 calc(100% - 7px))',
    chamfer: 'polygon(11px 0,calc(100% - 11px) 0,100% 11px,100% calc(100% - 11px),calc(100% - 11px) 100%,11px 100%,0 calc(100% - 11px),0 11px)',
})

/**
 * The same two shapes as BOX_CLIPS, cut smaller.
 *
 * A control is a fraction of the card's size, and reusing the card's 7px step
 * or 11px chamfer on a 2rem button eats the corners rather than shaping them —
 * the notch has to scale with the thing it's notching.
 */
const CONTROL_CLIPS = Object.freeze({
    pixel: 'polygon(0 3px,3px 3px,3px 0,calc(100% - 3px) 0,calc(100% - 3px) 3px,100% 3px,100% calc(100% - 3px),calc(100% - 3px) calc(100% - 3px),calc(100% - 3px) 100%,3px 100%,3px calc(100% - 3px),0 calc(100% - 3px))',
    chamfer: 'polygon(6px 0,calc(100% - 6px) 0,100% 6px,100% calc(100% - 6px),calc(100% - 6px) 100%,6px 100%,0 calc(100% - 6px),0 6px)',
})

/** The card's surface. Drawn over the overlay fill, under the contents. */
const BOX_MATERIALS = Object.freeze({
    marble: 'linear-gradient(114deg,transparent 38%,rgba(120,100,60,.14) 39.4%,transparent 41%),' +
        'linear-gradient(74deg,transparent 62%,rgba(120,100,60,.11) 63.4%,transparent 65%),' +
        'linear-gradient(100deg,transparent 20%,rgba(140,120,80,.09) 21%,transparent 23%)',
    wood: 'repeating-linear-gradient(92deg,rgba(80,45,15,.13) 0 2px,transparent 2px 9px),' +
        'repeating-linear-gradient(92deg,rgba(80,45,15,.07) 0 1px,transparent 1px 4px)',
    scan: 'repeating-linear-gradient(0deg,rgba(0,0,0,.32) 0 1px,transparent 1px 3px)',
    glass: 'linear-gradient(125deg,rgba(255,255,255,.22) 0 26%,transparent 52%)',
    paper: 'repeating-linear-gradient(0deg,rgba(255,235,200,.06) 0 1px,transparent 1px 5px)',
    leaded: 'repeating-linear-gradient(90deg,rgba(0,0,0,.30) 0 1.4px,transparent 1.4px 26px),' +
        'repeating-linear-gradient(0deg,rgba(0,0,0,.30) 0 1.4px,transparent 1.4px 26px)',
    bevel: 'linear-gradient(180deg,rgba(255,255,255,.32) 0 5px,transparent 5px),' +
        'linear-gradient(0deg,rgba(0,0,0,.28) 0 5px,transparent 5px)',
})

/** What each display face should fall back to while (or if) it fails to load. */
const FONT_FALLBACK = Object.freeze({
    'Silkscreen': 'monospace',
    'Bitcount': 'monospace',
    'Space Mono': 'monospace',
    'Orbitron': 'sans-serif',
    'Bungee': 'sans-serif',
    'Caveat': 'cursive',
    'Rye': 'serif',
    'Cinzel': 'serif',
    'Pirata One': 'serif',
    'Shippori Mincho': 'serif',
    'Rajdhani': 'sans-serif',
})

const loadedFonts = new Set()

/**
 * Loads a theme's display face, once, on demand.
 *
 * Not in index.html with Inter and Lora: there are a dozen of these across
 * the catalogue and a player sees exactly one, so shipping them all site-wide
 * would download eleven faces nobody asked for on every page of the site.
 * They're all Google Fonts (OFL/Apache), which is also why they can ship at
 * all — unlike bought artwork, the licence follows the file.
 */
export function loadThemeFont(family) {
    if (!family || loadedFonts.has(family) || typeof document === 'undefined') return
    loadedFonts.add(family)
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}&display=swap`
    document.head.appendChild(link)
}

/**
 * The theme's display face, for headings only.
 *
 * Body copy and every number stay on Inter whatever the theme: Silkscreen is
 * a personality, not something to read a stat block in.
 */
export function fontStyles(theme) {
    const family = theme.font
    return family ? { fontFamily: `'${family}', ${FONT_FALLBACK[family] || 'serif'}` } : null
}

/** A card: the theme's overlay fill, its text colour, and its accent. */
export function overlayStyles(theme) {
    return {
        backgroundColor: theme.colors.overlay,
        color: theme.colors.text,
        // Read by the tier-epic/legendary/mythical card rules — the accent
        // varies per theme, so it travels as a custom property rather than
        // being duplicated into every rule.
        '--tier-accent': theme.colors.accent,
        ...(theme.colors.glow && { boxShadow: theme.colors.glow }),
    }
}

/**
 * Just the theme's foreground colour, for controls that would otherwise sit
 * at Bulma's default grey — the recentre button reads as a smudge on a dark
 * theme and as nothing at all on a light one.
 */
export function textStyles(theme) {
    return { color: theme.colors.text }
}

/**
 * The page around the panel. See pageFrame() — the theme's own colour taken
 * down towards black, so the panel reads as mounted rather than as the only
 * styled thing on an unstyled page.
 */
export function pageStyles(theme) {
    const frame = pageFrame(theme)
    return frame ? { backgroundColor: frame } : null
}

/** The panel itself: the theme's background, sized for its scene. */
export function panelStyles(theme) {
    const styles = {
        background: theme.colors.background,
        color: theme.colors.text,
    }

    // A scene is composed for a band at the top rather than stretched over
    // the whole panel. The profile runs several screens tall, and scaling a
    // horizon to that puts the skyline — and anything standing on it —
    // halfway down the reward track. Below the band the last colour simply
    // continues.
    if (theme.sceneHeight) {
        styles.backgroundSize = `100% ${theme.sceneHeight}px`
        styles.backgroundRepeat = 'no-repeat'
        if (theme.ground) styles.backgroundColor = theme.ground
    } else if (theme.backgroundSize) {
        // Only patterned themes have a tile to size; a flat common theme
        // keeps `cover` from the stylesheet.
        styles.backgroundSize = theme.backgroundSize
    }
    return styles
}

/**
 * Escalates the Overview cards and heading font past what colour alone can
 * carry. Common and rare stay plain; epic adds a border in the theme's own
 * accent, legendary adds a sheen sweeping across each card, mythical doubles
 * the border into a frame.
 *
 * Overview only — the Stats and Customize panels deliberately don't ask for
 * it, which is why this is a function they simply don't call rather than
 * something they have to opt out of.
 */
export function tierClass(theme) {
    const rank = { common: 0, rare: 1, epic: 2, legendary: 3, mythical: 4 }
    const level = rank[theme.rarity] || 0
    if (level >= 4) return 'tier-mythical'
    if (level >= 3) return 'tier-legendary'
    if (level >= 2) return 'tier-epic'
    return null
}

/**
 * The profile card's own shape and material.
 *
 * Two shapes exist that `border-radius` cannot describe — stepped
 * (Blockworld) and chamfered (Arcade, High Noon) — so those use a clip-path.
 * The catch is that a clip-path also clips the element's border away, so a
 * clipped card can't carry one: boxFrameStyles() puts the frame behind it as
 * a padded plate in the same shape instead, and only a *rounded* card gets a
 * real CSS border.
 */
export function boxStyles(theme) {
    const box = theme.box
    if (!box) return null

    const styles = {
        borderRadius: box.arch
            // A dome. Needs the two-radius form — a single value can only
            // make a circle's quadrant, not a shallow arch.
            ? '52% 52% 5px 5px / 34% 34% 5px 5px'
            : (box.radius !== undefined ? box.radius : null),
    }
    if (box.clip) styles.clipPath = BOX_CLIPS[box.clip]
    else if (box.frame) styles.border = `2px solid ${box.frame}`
    if (box.shadow) styles.boxShadow = box.shadow
    // The dome eats into the card's top, so the contents move down out of it
    // rather than sitting inside the curve.
    if (box.arch) styles.paddingTop = '1.6rem'
    return styles
}

/** The plate behind a clipped card, standing in for the border it can't have. */
export function boxFrameStyles(theme) {
    const box = theme.box
    if (!box || !box.clip) return null
    return {
        padding: '3px',
        background: box.frame,
        clipPath: BOX_CLIPS[box.clip],
    }
}

export function boxMaterialStyles(theme) {
    const box = theme.box
    if (!box || !box.material) return null
    return { backgroundImage: BOX_MATERIALS[box.material] }
}

/**
 * Reserves the space the header occupies.
 *
 * It's absolutely positioned, so it takes no part in layout — without this it
 * would be drawn over the nav.
 */
export function boxWrapStyles(theme) {
    const header = theme.header
    return { '--header-height': header ? `${header.height + 6}px` : '0px' }
}

/** The architecture on top of the card, or null. */
export function headerStyles(theme) {
    const spec = theme.header
    if (!spec) return null
    // A header can be rail-only. Once the arcade sign's bulbs became a
    // repeating gradient, its drawn bulbs sat on top of the generated ones
    // and doubled up in the middle of the card — the drawing had nothing
    // left to add.
    const sprite = spec.sprite ? SPRITES[spec.sprite] : null
    if (!sprite && !spec.rail) return null
    if (!sprite) {
        return {
            viewBox: null,
            height: spec.height,
            body: null,
            fit: 'none',
            rail: { background: spec.rail, height: `${spec.railHeight || 100}%` },
            inset: spec.inset ? `${spec.inset}px` : null,
        }
    }
    return {
        viewBox: sprite.vb,
        height: spec.height,
        body: `<g fill="${spec.color}">${sprite.svg}</g>`,
        // A header spans the card, which is ~4.6x its drawn width. Stretching
        // to fill is right for something that genuinely is that wide — a hung
        // board — and wrong for anything with a fixed shape: it flattened the
        // pediment into a smear and turned the marquee's bulbs into ellipses.
        // `fit: center` keeps the drawing's aspect and centres it, and `rail`
        // draws the part that *should* run the full width (a cornice, a sign
        // board) behind it.
        fit: spec.fit === 'center' ? 'xMidYMid meet' : 'none',
        // Any CSS background, not just a colour — the arcade sign's bulbs are
        // a repeating gradient, which spaces them evenly at any card width
        // instead of stretching three drawn circles.
        rail: spec.rail
            ? { background: spec.rail, height: `${spec.railHeight || 30}%` }
            : null,
        inset: spec.inset ? `${spec.inset}px` : null,
    }
}

/**
 * The theme's silhouette, for controls that should read as part of the box
 * rather than as Bulma defaults dropped on top of it.
 *
 * Shape only — no colour. The claim button and the season chip each have
 * their own reason for the colour they carry, and neither should lose it just
 * to match a border.
 */
export function controlShapeStyles(theme) {
    const box = theme.box
    if (!box) return null

    const styles = {}
    if (box.clip) {
        styles.clipPath = CONTROL_CLIPS[box.clip]
    } else {
        if (box.radius !== undefined) styles.borderRadius = box.radius
        // A clipped control can't carry a border, same as the card — the clip
        // takes the border with it. Shape alone has to do it.
        if (box.frame) styles.border = `1px solid ${box.frame}`
    }
    return styles
}

/**
 * Anything filled with the theme's accent: the claim button, the
 * current-level badge.
 *
 * Bulma's `is-success` green is the same green on every theme, which reads as
 * a control someone forgot to style. The accent is the colour the theme
 * already nominates for exactly this. The label colour is *derived* rather
 * than fixed white, because accents run from near-black to pale cyan and
 * white on the latter is unreadable.
 */
export function accentStyles(theme) {
    const accent = theme.colors.accent
    return { backgroundColor: accent, color: contrastOn(accent) }
}
